'use client';

import { useState, useRef, useEffect } from 'react';
import { Play, Pause, Maximize, Volume2, VolumeX } from 'lucide-react';
import { logger } from '@/lib/logger';

interface VideoPlayerProps {
  src: string;
  poster?: string;
  className?: string;
}

export function VideoPlayer({ src, poster, className = '' }: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showControls, setShowControls] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleError = (e: Event) => {
      logger.error('[VideoPlayer] Video load error:', e);
      setError('Failed to load video file');
      setIsLoading(false);
    };

    const handleCanPlay = () => {
      setIsLoading(false);
      setError(null);
      logger.log('[VideoPlayer] Video can play');
    };

    const handleLoadedMetadata = () => {
      logger.log('[VideoPlayer] Video metadata loaded');
      setIsLoading(false);
    };

    const handleLoadStart = () => {
      setIsLoading(true);
      setError(null);
    };

    video.addEventListener('error', handleError);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('loadstart', handleLoadStart);

    // Try to load the video
    video.load();

    return () => {
      video.removeEventListener('error', handleError);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('loadstart', handleLoadStart);
    };
  }, [src]);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.pause();
      setIsPlaying(false);
    } else {
      video.play().catch(err => {
        logger.error('[VideoPlayer] Play error:', err);
        setError('Failed to play video');
        setIsPlaying(false);
      });
      setIsPlaying(true);
    }
  };

  const toggleFullscreen = () => {
    const video = videoRef.current;
    if (!video) return;

    if (document.fullscreenElement) {
      document.exitFullscreen().catch(err => {
        logger.error('[VideoPlayer] Exit fullscreen error:', err);
      });
    } else {
      video.requestFullscreen().catch(err => {
        logger.error('[VideoPlayer] Enter fullscreen error:', err);
      });
    }
  };

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <svg className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-red-800 dark:text-red-200 mb-1">
              Failed to load video file
            </p>
            <p className="text-xs text-red-600 dark:text-red-400 break-all mb-2">
              {src}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setError(null);
                  const video = videoRef.current;
                  if (video) {
                    video.load();
                  }
                }}
                className="text-xs px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
              >
                Try again
              </button>
              <button
                onClick={() => window.location.reload()}
                className="text-xs px-3 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
              >
                Refresh page
              </button>
            </div>
            <p className="text-xs text-red-600 dark:text-red-400 mt-2">
              This may be a storage bucket permission issue. Check that the file exists and the bucket is public.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`relative rounded-lg overflow-hidden bg-black ${className}`}
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => setShowControls(false)}
    >
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
          <div className="animate-spin h-10 w-10 border-4 border-white border-t-transparent rounded-full" />
        </div>
      )}
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        controls
        controlsList="nodownload"
        playsInline
        preload="metadata"
        className="w-full max-h-[600px] object-contain"
        style={{ display: isLoading ? 'none' : 'block' }}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        crossOrigin="anonymous"
      >
        Your browser does not support video playback.
      </video>

      {/* Custom play button overlay when paused and not loading */}
      {!isPlaying && !showControls && !isLoading && !error && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
          <button
            onClick={togglePlay}
            className="w-16 h-16 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center transition-colors"
            aria-label="Play video"
          >
            <Play className="w-8 h-8 text-white fill-white" />
          </button>
        </div>
      )}
    </div>
  );
}
